import DropdownSelectMultiOption from 'components/Common/DropdownSelectMultiOption';
import InputsRange from 'components/Common/InputsRange';
import LabelWrapper from 'components/Common/LabelWrapper';
import TableContainer from 'components/Common/TableContainer';
import { useRole } from 'components/Hooks/UserHooks';
import { formatNumberWithCommas } from 'helpers/format';
import { ROLES_FOR_APP, isHavePermissionRole } from 'helpers/role';
import ModalImportGenerateNewStock from 'pages/News/BoostingNews/ModalImportGenerateNewStock';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CountUp from 'react-countup';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from 'react-redux';
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CardBody, CardHeader, Col, Input, Row, Spinner } from "reactstrap";
import { createSelector } from 'reselect';
import { getTossStocks as onGetTossStocks } from 'store/thunks';
import { NumberParam, StringParam, useQueryParams, withDefault, } from "use-query-params";
import NestingFilter from '../../News/Components/NestingFilter';
import ModalConfigGenerate from '../ModalConfigGenerate';
import ModalGenerateNewStock from '../ModalGenerateNewStock';
import { CONFIG_OPTION_TOAST_INFO } from 'common/toast';

const TYPE_SELECT_DEFAULT: string = 'upload_date';

const TossList = (activeTab?: any) => {
    const { t, i18n } = useTranslation();
    const { userPermissions } = useRole();

    const LOCAL_SELECT_OPTIONS = [{ label: t('Domestic'), value: 'only_domestic' }, { label: t('Foreign'), value: 'only_foreign' }];
    const TYPE_SELECT_OPTIONS = [{ label: t('Only Up'), value: 'only_up' }, { label: t('Only Down'), value: 'only_down' }];

    const [query, setQuery]: any = useQueryParams({
        time_request: withDefault(NumberParam, 0),
        start_rank: withDefault(StringParam, '1'),
        end_rank: withDefault(StringParam, ''),
        min_pct: withDefault(StringParam, ''),
        max_pct: withDefault(StringParam, ''),
        min_price: withDefault(StringParam, ''),
        up_check: withDefault(StringParam, ''),
        down_check: withDefault(StringParam, ''),
        only_domestic: withDefault(StringParam, ''),
        only_foreign: withDefault(StringParam, ''),

    });

    const [startRankSearch, setStartRankSearch] = useState<string>(query?.start_rank || "");
    const [endRankSearch, setEndRankSearch] = useState<string>(query?.end_rank || "");
    const [minPctSearch, setMinPctSearch] = useState<string>(query?.min_pct || "");
    const [maxPctSearch, setMaxPctSearch] = useState<string>(query?.max_pct || "");
    const [minPriceSearch, setMinPriceSearch] = useState<string>(query?.min_price || "");

    const [localSearch, setLocalSearch] = useState<any>(LOCAL_SELECT_OPTIONS?.filter((item: any) => ([query?.only_domestic ? 'only_domestic' : '', query?.only_foreign ? 'only_foreign' : '']).includes(item?.value)));
    const [typeSearch, setTypeSearch] = useState<any>(TYPE_SELECT_OPTIONS?.filter((item: any) => ([query?.up_check ? 'only_up' : '', query?.down_check ? 'only_down' : '']).includes(item?.value)));

    const [isOpenModalConfigPrompt, setIsOpenModalConfigPrompt] = useState<boolean>(false);

    const [isOpenImportGenerateNewsStock, setIsOpenImportGenerateNewsStock] = useState<boolean>(false);

    const dispatch: any = useDispatch();

    const selectLayoutState = (state: any) => state.News;

    const NewsProperties = createSelector(
        selectLayoutState,
        (state) => ({
            tossStocks: state.tossStocks,
            isTossStockSuccess: state.isTossStockSuccess,
            isTossStockLoading: state.isTossStockLoading,
            error: state.error,
        })
    );

    // Inside your component
    const { tossStocks, error, isTossStockLoading } = useSelector(NewsProperties);

    useEffect(() => {
        dispatch(onGetTossStocks(query));
    }, [JSON.stringify(query)]);

    const searchData = () => {

        const queryNew = {
            ...query,
            start_rank: startRankSearch,
            end_rank: endRankSearch,
            min_pct: minPctSearch,
            max_pct: maxPctSearch,
            min_price: minPriceSearch,
            up_check: typeSearch?.map((item: any) => item?.value)?.includes('only_up') ? '1' : '',
            down_check: typeSearch?.map((item: any) => item?.value)?.includes('only_down') ? '1' : '',
            only_domestic: localSearch?.map((item: any) => item?.value)?.includes('only_domestic') ? '1' : '',
            only_foreign: localSearch?.map((item: any) => item?.value)?.includes('only_foreign') ? '1' : '',
            page: 1,
            time_request: + new Date()
        };
        setQuery(queryNew);
    };

    const resetData = () => {
        const queryNew = {
            start_rank: '1',
            end_rank: '',
            min_pct: '',
            max_pct: '',
            min_price: '',
            up_check: '',
            down_check: '',
            only_domestic: '',
            only_foreign: '',
            sort_by: TYPE_SELECT_DEFAULT,
            order_by: 'DESC',
            page: 1,
            time_request: + new Date(),
        };

        setQuery(queryNew, "push");
        setStartRankSearch((_prev) => '1');
        setEndRankSearch((_prev) => '');
        setMinPctSearch((_prev) => '');
        setMaxPctSearch((_prev) => '');
        setMinPriceSearch((_prev) => '');
        setTypeSearch((_prev: any) => null);
        setLocalSearch((_prev: any) => null);
    };

    const triggerRefresh = () => {
        dispatch(onGetTossStocks(query));
        setIsOpenImportGenerateNewsStock((_prev) => false);
    };

    // Column
    const columns = useMemo(
        () => [
            {
                Header: t('Rank'),
                accessor: "rank",
                filterable: true,
                sortable: false,
                thWidth: 70,
                thClass: "align-middle text-start",
                Cell: (cell: any) => (
                    <div className="text-start" style={{ minWidth: '50px' }}>
                        {cell?.value}
                    </div>
                ),
            },
            {
                Header: t('Stock Name'),
                accessor: "stock_name",
                filterable: false,
                sortable: false,
                thClass: "align-middle text-start",
                thWidth: 150,
                Cell: (cell: any) => {
                    const item: any = cell?.row?.original;
                    return (
                        <div style={{ minWidth: '100px' }} className="text-start fs-15">
                            {item?.stock_name}
                        </div>
                    );
                }
            },
            {
                Header: t('Current Price (KRW)'),
                accessor: "current_price",
                filterable: true,
                sortable: false,
                thClass: 'text-end align-middle',
                thWidth: 130,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (<>
                        <div className="text-end pe-3" style={{ minWidth: '100px' }}>
                            {formatNumberWithCommas(cell?.value || 0)}
                        </div>
                    </>)
                },
            },
            {
                Header: t('Change'),
                accessor: "change",
                filterable: true,
                sortable: false,
                thWidth: 150,
                thClass: 'align-middle text-end',
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    const textColor = String(item?.change).toUpperCase() === 'UP' ? 'text-danger' : 'text-info';
                    return (<div style={{ minWidth: '100px' }} className="text-end">
                        <span className={textColor}>
                            {item?.change}
                        </span>
                    </div>)
                },
            },
            {
                Header: t('Change Rate (%)'),
                accessor: "change_rate",
                filterable: true,
                sortable: false,
                thWidth: 150,
                thClass: 'align-middle text-end',
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (<div style={{ minWidth: '100px' }} className="text-end">
                        {item?.change_rate} %
                    </div>)
                },
            },
            {
                Header: t('Product Code'),
                accessor: "product_code",
                filterable: true,
                sortable: false,
                thWidth: 150,
                thClass: 'align-middle text-end',
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (<>
                        <div className="text-end" style={{ minWidth: '100px' }}>{item?.product_code || '-'}</div>
                    </>)
                },
            },

        ],
        [i18n?.language, query]
    );
    const handleChangePage = useCallback((page: any) => {
        setQuery({ page: page + 1 })
    }, []);

    const handleChangeSorting = useCallback((sortBy: any) => {
        setQuery((_prev: any) => {
            return ({ ..._prev, ...sortBy, page: 1 });
        });
    }, []);

    const handleCloseImportGenerateNewsStock = () => {
        setIsOpenImportGenerateNewsStock((_prev) => false);
    };

    const [isOpenModalGenerate, setIsOpenModalGenerate] = useState<boolean>(false);

    const handleShowModalGenerate = () => {
        setIsOpenModalGenerate((_prev) => true);
    };

    const handleCloseModalGenerate = () => {
        setIsOpenModalGenerate((_prev) => false);
    };

    const handleCopyStockName = () => {
        const stockNames = tossStocks?.map((item: any) => item?.stock_name);
        const stockNamesString = stockNames?.join('\n');
        navigator.clipboard.writeText(stockNamesString);
        toast(`${t('Copied Successfully')}`, CONFIG_OPTION_TOAST_INFO);
    };

    return (
        <React.Fragment>
            <Row>
                {isHavePermissionRole(ROLES_FOR_APP.NEWS_RECOMPOSITION, userPermissions) && (
                    <Col lg={12}>
                        <CardHeader className="border-0" style={{ borderRadius: '30px' }}>
                            <Row className="g-4 align-items-center">
                                <div className="col-md-4 col-lg-5 mt-5 mt-sm-4">
                                    <div>
                                        <h5 className="card-title mb-0 d-flex flex-wrap gap-1 gap-xxl-2">
                                            <span>
                                                <span className="me-2">{t('Total')}:</span>
                                                {(isTossStockLoading && query?.page === 1) ? <Spinner size="sm" className="text-primary"></Spinner> : (<CountUp
                                                    start={0}
                                                    end={tossStocks?.length || 0}
                                                    duration={1}
                                                    className="text-primary"
                                                />)}
                                            </span>
                                        </h5>
                                    </div>
                                </div>
                                <div className="col-md-8 col-lg-7 text-end">
                                    {/* {isHavePermissionRole(ROLES_FOR_APP.NEWS_RECOMPOSITION, userPermissions) && (<button
                                        type="button"
                                        className="btn btn-success add-btn rounded-pill mb-2 mb-sm-0"
                                        onClick={() => setIsOpenImportGenerateNewsStock((_prev) => true)}
                                    >
                                        <i className="ri-upload-line align-bottom"></i>
                                        <span className="ms-1">{t('Button Import Bulk Generate Boosting News V2')}</span>
                                    </button>)} */}
                                    {isHavePermissionRole(ROLES_FOR_APP.CONFIGURATION_BOOSTING_NEWS_V2, userPermissions) && (<button
                                        type="button"
                                        className="btn btn-primary add-btn rounded-pill ms-2"
                                        onClick={() => setIsOpenModalConfigPrompt((_prev: any) => true)}
                                    >
                                        <i className="ri-settings-line align-bottom"></i>
                                        <span className="ms-1">{t('Config Generate')}</span>
                                    </button>)}
                                </div>
                            </Row>
                        </CardHeader>
                        <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-0 pb-md-4 px-0 px-lg-3 pt-0 pt-lg-3">
                            <NestingFilter>
                                <Row className="g-4 align-items-end mt-0">
                                    <Col sm={6} md={6} lg={3} xl={3} xxl={3} className='mt-3'>
                                        <InputsRange
                                            nameFrom="from"
                                            nameTo="to"
                                            valueFrom={startRankSearch}
                                            valueTo={endRankSearch}
                                            placeholderFrom={`${t('Rank (Start)')}...`}
                                            placeholderTo={`${t('Rank (End)')}...`}
                                            labelFrom={`${t('Rank (Start)')}`}
                                            labelTo={`${t('Rank (End)')}`}
                                            isTypeNumber={true}
                                            onChangeFrom={(val: any) => setStartRankSearch(val)}
                                            onChangeTo={(val: any) => setEndRankSearch(val)}
                                            onKeyDownFrom={(e: any) => {
                                                if (e.key === "Enter") {
                                                    searchData();
                                                }
                                            }}
                                            onKeyDownTo={(e) => {
                                                if (e.key === "Enter") {
                                                    searchData();
                                                }
                                            }}
                                        />
                                    </Col>
                                    <Col sm={6} md={6} lg={3} xl={3} xxl={3} className='mt-3'>
                                        <InputsRange
                                            nameFrom="from"
                                            nameTo="to"
                                            valueFrom={minPctSearch}
                                            valueTo={maxPctSearch}
                                            placeholderFrom={`${t('Min Pct (Start)')}...`}
                                            placeholderTo={`${t('Max Pct (End)')}...`}
                                            labelFrom={`${t('Min Pct (Start)')}`}
                                            labelTo={`${t('Max Pct (End)')}`}
                                            isTypeNumber={true}
                                            onChangeFrom={(val: any) => setMinPctSearch(val)}
                                            onChangeTo={(val: any) => setMaxPctSearch(val)}
                                            onKeyDownFrom={(e: any) => {
                                                if (e.key === "Enter") {
                                                    searchData();
                                                }
                                            }}
                                            onKeyDownTo={(e) => {
                                                if (e.key === "Enter") {
                                                    searchData();
                                                }
                                            }}
                                        />
                                    </Col>
                                    <Col sm={6} md={6} lg={2} xl={2} xxl={2} className='mt-3'>
                                        <LabelWrapper label={t('Min Price')} isShow={true}>
                                            <Input
                                                name="min_price"
                                                value={formatNumberWithCommas(minPriceSearch)}
                                                placeholder={`${t('Min Price')}...`}
                                                type="text"
                                                autoComplete="off"
                                                className="form-control search"
                                                onChange={(val: any) => {
                                                    const inputValue = String(val.target.value).replace(/[^0-9]/g, '');
                                                    setMinPriceSearch(inputValue);
                                                }}
                                                onKeyDown={(e: any) => {
                                                    if (e.key === "Enter") {
                                                        searchData();
                                                    }
                                                }}
                                            />
                                        </LabelWrapper>
                                    </Col>

                                    <Col sm={6} md={6} lg={2} xl={2} xxl={2} className='mt-3'>
                                        <LabelWrapper label={t('Type')} isShow={true}>
                                            <DropdownSelectMultiOption
                                                name={`type`}
                                                dataList={LOCAL_SELECT_OPTIONS}
                                                placeholder={`${t("Type")}...`}
                                                className="search-filter-category-type dropdown-status-rounded"
                                                classNamePrefix="name-prefix"
                                                initialValue={localSearch || null}
                                                isHaveMoreInfo={true}
                                                onChangeSelect={(e: any) => {
                                                    setLocalSearch(e)
                                                }}
                                                isHasOptionAll={true}
                                            />
                                        </LabelWrapper>
                                    </Col>

                                    <Col sm={6} md={6} lg={2} xl={2} xxl={2} className='mt-3'>
                                        <LabelWrapper label={t('Change')} isShow={true}>
                                            <DropdownSelectMultiOption
                                                name={`change`}
                                                dataList={TYPE_SELECT_OPTIONS}
                                                placeholder={`${t("Change")}...`}
                                                className="search-filter-category-type dropdown-status-rounded"
                                                classNamePrefix="name-prefix"
                                                initialValue={typeSearch || null}
                                                isHaveMoreInfo={true}
                                                onChangeSelect={(e: any) => {
                                                    setTypeSearch(e)
                                                }}
                                                isHasOptionAll={true}
                                            />
                                        </LabelWrapper>
                                    </Col>

                                    <Col sm={6} md={6} lg={12} xl={12} xxl={12} className="order-3 order-xxl-0 hstack gap-1 justify-content-center justify-content-sm-end justify-content-lg-end justify-content-xl-end justify-content-xxl-end mt-3 mt-xxl-3">
                                        <div>
                                            <button
                                                type="button"
                                                className="btn btn-primary me-2 me-lg-2"
                                                onClick={searchData}
                                                disabled={isTossStockLoading}
                                            >
                                                <i className="ri-search-line align-bottom me-1"></i>{" "}
                                                {t('Button Search')}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-secondary fs-14"
                                                onClick={resetData}
                                            >
                                                <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                                                {t('Button Reset')}
                                            </button>
                                        </div>
                                    </Col>
                                </Row>
                            </NestingFilter>
                        </div>
                        <div className="border border-dashed border-top-0 border-end-0 border-start-0 px-3 py-3">
                            <Col sm={12} md={12} xl={12} className="my-0 mt-2 mt-md-0 text-start d-flex flex-wrap gap-3 justify-content-between align-items-center">
                                {isHavePermissionRole(ROLES_FOR_APP.NEWS_TOSS_STOCK, userPermissions) && <div
                                    className='d-flex flex-wrap gap-3 justify-content-between align-items-center'
                                >
                                    <button
                                        type="button"
                                        className="btn btn-soft-success"
                                        disabled={tossStocks?.length === 0}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleShowModalGenerate();
                                        }}
                                    >
                                        {t('Generate News')}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-soft-info"
                                        disabled={tossStocks?.length === 0}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopyStockName();
                                        }}
                                    >
                                        {t('Copy Stock Name')}
                                    </button>
                                </div>}
                                <div className="mt-3 mt-md-0">

                                </div>
                            </Col>
                        </div>
                        <CardBody className="pt-0 px-0">
                            <div className="mx-3 my-4 mt-3">
                                <TableContainer
                                    className="custom-header-css"
                                    divClass="table-responsive table-card "
                                    tableClass="align-middle sticky-table-boosting-news-recomposition"
                                    theadClass="table-light"
                                    columns={columns}
                                    data={tossStocks?.length ? tossStocks : []}
                                    customPageSize={query.limit}
                                    customPageIndex={query.page - 1}
                                    totalRecords={tossStocks?.length}
                                    customPageCount={1}
                                    handleChangePage={handleChangePage}
                                    manualSorting={true}
                                    sorting={{ sort_by: query.sort_by, order_by: query.order_by }}
                                    handleChangeSorting={handleChangeSorting}
                                    isLoading={isTossStockLoading}
                                    isShowPagination={false}
                                    isShowLoadingBottom={false}
                                    isScrollToTop={true}
                                />
                            </div>
                        </CardBody>
                    </Col>
                )}
            </Row>
            <ToastContainer closeButton={false} newestOnTop={false} className="toast---------text" pauseOnFocusLoss autoClose={2000} limit={2} />

            <ModalImportGenerateNewStock
                isOpen={isOpenImportGenerateNewsStock}
                onRefresh={triggerRefresh}
                onClose={handleCloseImportGenerateNewsStock}
            />

            <ModalGenerateNewStock
                isOpen={isOpenModalGenerate}
                params={query}
                onRefresh={handleCloseModalGenerate}
                onClose={handleCloseModalGenerate}
            />

            <ModalConfigGenerate
                isOpen={isOpenModalConfigPrompt}
                onClose={() => setIsOpenModalConfigPrompt((_prev: any) => false)}
            />

        </React.Fragment >
    );
};

export default TossList;